import {
  Card,
  CardResponseMeta,
  Functionality,
  Language,
  Operator,
  Pointer,
  ViewResponse,
} from '@yleisradio/areena-types';
import { Focus } from './navigation/typings';
import {
  AreenaApiClient,
  LoggedInUser,
  Mode,
  Profile,
  ProfileIcon,
  ProfileCookies,
} from '../common/types';
import {
  CREDENTIALS_LOGIN_SUB_PATH,
  PAIR_LOGIN_SUB_PATH,
  REGISTER_LOGIN_SUB_PATH,
} from './Routes/routeNames';
import {
  Location,
  NavigateFunction,
  NavigateOptions,
  To,
} from 'react-router-dom';
import { ErrorName } from './error/errors';

export type AreenaApiVersion = '10' | 'edge';

export type DefaultAssetVersion = string;

export interface LastFocusState {
  [id: string]: Focus;
}

export interface DefaultAssetVersionResponse {
  images: { defaultAssetVersion: DefaultAssetVersion };
}

export enum GeoLocation {
  Finland = 'Finland',
  World = 'World',
  PortabilityRegion = 'PortabilityRegion',
}

export interface CurrentAddress {
  city?: string;
  city_region?: string;
  asnum?: string;
  geoLocation: GeoLocation;
  countryCode?: string;
}

export interface AreenaApiRequiredParams {
  v: AreenaApiVersion;
  language: Language;
  client: AreenaApiClient;
}

export type SelfURIMap = {
  [mode in Mode]?: string;
};

export type NavItemType =
  | 'view'
  | 'tab'
  | 'list'
  | 'card'
  | 'grid-card'
  | 'back'
  | 'key'
  | 'control'
  | 'sidebar-item'
  | 'responsive-grid-card'
  | 'subtitle'
  | 'audio'
  | 'intro-outro'
  | 'add-favorite'
  | 'html-element'
  | 'language-card';

export interface NavItem {
  id: string;
  type: NavItemType;
  functionality?: Functionality;
  pointer?: Pointer;
}

export type LoadedRange = [number, number];

export type PaginationData = Pick<
  CardResponseMeta,
  'offset' | 'limit' | 'count'
>;

export interface ViewList {
  [id: string]: ViewResponse;
}

export interface ViewPayload {
  viewId: string;
  view: ViewResponse;
}

export type EditableProfileField = keyof Pick<Profile, 'displayName' | 'icon'>;

export interface FocusableCardList {
  cards: Card[];
  cardIndexOffset?: number;
  focus: Focus;
  focusParentId: string;
  hasFocus: boolean;
  isFocusHidden?: boolean;
  title?: string;
  hasLimelight?: boolean;
}

export type FocusableCard = Card & {
  isInPlayerView?: boolean;
  cardId?: string;
};

export interface Device {
  version: string;
  capabilities: string;
  vendor: string;
  model: string;
  software: string;
  hardware: string;
  family: string;
  year: string;
}

export type ScrollDirection = 'up' | 'right' | 'down' | 'left';

export type Scrolling = {
  canScroll: Record<ScrollDirection, boolean>;
  trigger: (direction: ScrollDirection) => void;
};

export const AudioType: MediaType = 'AudioObject';

export type MediaType = 'AudioObject' | 'VideoObject';

export type MediaLanguageCode =
  | 'fin'
  | 'swe'
  | 'eng'
  | 'fra'
  | 'deu'
  | 'dan'
  | 'nor'
  | 'sme'
  | 'ita'
  | 'spa'
  | 'rus'
  | 'por'
  | 'isl'
  | 'ukr'
  | 'und';

export type LegacyMediaHoHLanguageCodes = 'fih' | 'svh';

export type LegacyMediaLanguageCode =
  | 'fi'
  | 'qfi'
  | 'sv'
  | 'qsw'
  | 'en'
  | 'fr'
  | 'de'
  | 'da'
  | 'no'
  | 'se'
  | 'it'
  | 'es'
  | 'ru'
  | 'pt'
  | 'is';

export type SubtitleTrackKind = 'hardOfHearing' | 'translation';

export interface MediaTrack {
  index: number;
  language:
    | LegacyMediaLanguageCode
    | LegacyMediaHoHLanguageCodes
    | MediaLanguageCode;
  label?: string;
}

type CuePointKind = 'intro' | 'outro';

export interface CuePoint {
  kind: CuePointKind;
  start_time: number;
  end_time: number;
}

export interface CuePointItem {
  start: number;
  end: number;
}

export interface CuePointData {
  intro: CuePointItem | undefined;
  outro: CuePointItem | undefined;
}

export interface TrackButton extends DropdownOption {
  language: string;
  kind?: AudioTrackType | SubtitleTrackKind;
}

export interface DropdownOption {
  label: string;
  action: () => void;
}

export type AudioTrackType =
  | 'AudioTrack'
  | 'AudioSubtitling'
  | 'AudioDescription';

export interface AudioItem {
  language: MediaLanguageCode;
  label?: string;
  format?: AudioTrackFormat;
  index?: number;
  kind: AudioTrackType;
}

export interface SubtitleItem {
  language: MediaLanguageCode;
  type: SubtitleTrackType;
  kind: SubtitleTrackKind;
  index?: number;
  uri?: string;
}

export interface StreamComponents {
  audioTracks: AudioItem[];
  subtitles: SubtitleItem[];
}

type AudioTrackFormat = 'stereo' | 'mono' | 'dolby-digital-5.1';
export type SubtitleTrackType = 'CaptionTrack';

interface ScheduleAudioTrack {
  type: AudioTrackType;
  format: AudioTrackFormat;
  language: MediaLanguageCode;
  trackIndex: number;
  trackKey: string;
}

interface ScheduleSubtitleTrack {
  type: SubtitleTrackType;
  kind: SubtitleTrackKind;
  language: MediaLanguageCode;
  trackIndex: number;
  trackKey: string;
}

export interface ServiceScheduleItem {
  startTime: string;
  endTime: string;
  audioTracks: ScheduleAudioTrack[];
  subtitles: ScheduleSubtitleTrack[];
}

export type ScrubberTiles = {
  height: number;
  width: number;
  interval: number;
};

export type ScrubberImages = {
  matrix: {
    width: number;
    height: number;
  };
  tiles: ScrubberTiles;
  url: string;
};

export type MediaItem = {
  url: string;
  subtitles?: SubtitleItem[];
  audioTracks?: AudioItem[];
  audioBitrateKbps?: number;
  videoBitrateKbps?: number;
  multiaudio?: boolean;
  multibitrate?: boolean;
  type: MediaType;
  formatOf: string;
  live: boolean;
  cuepoints: CuePoint[];
  serviceSchedule?: ServiceScheduleItem[];
  dvrWindow?: string;
  width: number;
  height: number;
  scrubber_images?: ScrubberImages;
};

export type MediaProtocol = 'dash' | 'hls' | 'cmaf-hls' | 'pmd';

export type MediaApiResponse = {
  hls?: MediaItem;
  'cmaf-hls'?: MediaItem;
  dash?: MediaItem;
  pmd?: MediaItem[];
};

export type SeekDirection = 'forward' | 'none' | 'back';

export interface Comscore {
  yle_referer?: string;
  countername?: string;
  yle_id?: string;
  yle_interaction?: string;
  yle_sk?: string;
  yle_language?: string;
}

type WatchedDurationSeconds = number;

export interface PlaybackHistoryEntries {
  [itemId: string]: WatchedDurationSeconds;
}

export interface OperatorResponse {
  data: Operator;
  meta: CardResponseMeta;
}

export interface ReplaceSettingsViewDataPayload {
  data: SettingsViewState;
}

export interface UpdateSettingsViewDataPayload {
  htmlDocument?: string | undefined;
  tabIndex: number;
  type: SettingsViewTab;
}

export type SettingsViewTab =
  | 'start-view'
  | 'close-app'
  | 'logout'
  | 'consent-settings'
  | 'contact-page'
  | 'data-protection'
  | 'accessibility-statement'
  | 'device-info'
  | 'instructions'
  | 'language-change';

export interface SettingsViewContent {
  htmlDocument?: string | undefined;
  title: string;
  type: SettingsViewTab;
}

export type SettingsViewState = SettingsViewContent[];

export type LoginSubRoute =
  | typeof PAIR_LOGIN_SUB_PATH
  | typeof REGISTER_LOGIN_SUB_PATH
  | typeof CREDENTIALS_LOGIN_SUB_PATH;

export enum PollerResponseStatus {
  Connected = 'connected',
  NotConnected = 'not_connected',
  Error = 'error',
}

type PollerResponseConnectedData = LoginData;

export interface PollerResponse {
  status: PollerResponseStatus;
  data?: PollerResponseConnectedData;
}

export interface LoginData {
  user: LoggedInUser;
  profileCookies: ProfileCookies;
}

export interface LoginResponse {
  success?: string;
  error?: string;
  data?: LoginData;
}

export type QueryParams = {
  [key: string]: string;
};

export type Cookies = {
  [key: string]: string;
};

export interface AdobeLabels {
  [key: string]: string | number;
}

export type AdobePlayerStateObject = {
  'customstate.name': string;
};

export type PlayerAnalyticsStateType = 'audio' | 'subtitle';

export type PlayerAnalyticsState = {
  type?: PlayerAnalyticsStateType;
  current: boolean;
  stateObject: AdobePlayerStateObject;
};

export interface AdobeAnalytics {
  hasSession: boolean;
  adobeLabels: AdobeLabels;
  programId: string;
  mediaDuration: number;
  mediaType: MediaType;
  isLiveMedia: boolean;
  tracker: any;
  appMeasurement: any;
  playerStates: PlayerAnalyticsState[];
  initAnalytics: () => void;
  initMediaAnalytics: (
    playerLabels: AdobeLabels,
    programId: string,
    seek: number | undefined,
    user: LoggedInUser | null | undefined,
    appLabels: AdobeAppLabels,
    defaultAudio: AudioItem | null,
    mediaType: MediaType,
    mediaDuration: number,
    isLive: boolean
  ) => void;
  initPlayerStates: () => void;
  updatePlayerState: (
    type: PlayerAnalyticsStateType,
    item: AudioItem | SubtitleItem | null
  ) => void;
  activateSession: () => void;
  endSession: () => void;
  completed: () => void;
  played: () => void;
  paused: () => void;
  seekStart: () => void;
  seekEnd: () => void;
  bufferStart: () => void;
  bufferEnd: () => void;
  ended: () => void;
}

export interface AdobeAppLabels {
  'yle.vrsDevice': string;
  'yle.vrsProduct': string;
  'yle.vrsPlatform': string;
  Resolution: string;
  DeviceName: string;
  OSVersion: string;
  yle_client: string;
  yle_country_code?: string;
  yle_ab_string: string;
  yle_selva: string;
  yle_rec: string;
  yle_referer?: string;
  userconsent: string;
  yle_pageloadid?: string;
  profile_age_group?: number;
}

export enum ConsentAction {
  EDIT = 'edit-consent',
  ACCEPT_ALL = 'accept-all',
  ACCEPT_SELECTED = 'accept-selected',
  ACCEPT_NECESSARY = 'accept-necessary',
  SELECT_LANGUAGE = 'select-language',
  SELECT_FI = 'fi',
  SELECT_SV = 'sv',
}

export interface ConsentResponse {
  consentVersion: string;
  localizations: ConsentLocalizations;
}

export type MutableConsentName = 'development' | 'embedded_social_media';

export type ConsentName = MutableConsentName | 'required';

export type ConsentLabels = Record<ConsentName, ConsentLabel>;

export interface ConsentLabel {
  label: string;
  body: string;
  moreInfoLabel: string;
  lessInfoLabel: string;
  visibility: 'all' | 'loggedOut' | 'loggedIn';
}

export interface Consents {
  acceptAll: string;
  acceptNecessary: string;
  minimal: {
    header: string;
    customize: string;
  };
  customize: {
    header: string;
    acceptSelected: string;
    labels: ConsentLabels;
  };
}

export interface ConsentContent {
  info: ConsentInfo;
  consents: Consents;
}

export interface ConsentInfo {
  header: string | undefined;
  body: string | undefined;
  link: string | undefined;
  cookieInfoLink: string;
  loggedIn: {
    header: string;
    body: string;
    settingsInfo: string;
  };
}

export type ConsentLocalizations = Record<Language, ConsentContent>;

export enum MultiaudioUIstate {
  main = 'main',
  subtitle = 'subtitle',
  audio = 'audio',
  subtitleBackground = 'background',
  subtitleSize = 'size',
}

export interface Promotions {
  consentViewSeenDate?: string;
  loginLastSeenDate?: string;
  userLastLoginDate?: string;
  profileLastSeenDate?: string;
  profileTotalShowCount?: number;
}

export interface TestMedia {
  title: string;
  description: string;
  imageUrl: string;
  mediaUrl: string;
}

export interface TestPagePayload {
  data: TestMedia[];
}

export type SubtitleBackgroundOption = 'transparent' | 'white' | 'black';

export type SubtitleSizeOption = 'small' | 'normal' | 'large';

export type SubtitleOption = SubtitleBackgroundOption | SubtitleSizeOption;

export type SubtitleOptionType = 'size' | 'background';

type NoSubtitles = null;
type SubtitlesNotSet = undefined;
type AudioNotSet = undefined;

export type SubtitleLanguageMap = Partial<Record<SubtitleTrackKind, string>>;
export type AudioLanguageMap = Partial<Record<AudioTrackType, string>>;
export type LegacyLanguageCodeMap = Partial<
  Record<
    LegacyMediaLanguageCode | LegacyMediaHoHLanguageCodes,
    MediaLanguageCode
  >
>;

export type SubtitleSettings = {
  subtitles: SubtitleLanguageMap | SubtitlesNotSet | NoSubtitles;
  subtitleBackground: SubtitleBackgroundOption;
  subtitleSize: SubtitleSizeOption;
};

type AudioSettings = {
  audio: AudioLanguageMap | AudioNotSet;
};

export type StorageState = 'ssr' | 'client';

export type SubtitleSettingsStorage = {
  userSettings: {
    [username: string]: SubtitleSettings;
  };
  state: StorageState;
};

export type AudioSettingsStorage = {
  userSettings: {
    [username: string]: AudioSettings;
  };
  state: StorageState;
};

export type DataAttributes = {
  [dataAttribute: `data-${string}`]: string | undefined;
};

export type ProfileEditor = {
  displayName: string | null;
  icon: ProfileIcon | null;
};

export type ReactRouterLocationState = {
  from?: Location<ReactRouterLocationState>;
  areenaApiUri?: string;
  shouldHideSidebar?: boolean;
  loginParameters?: { username: string; password: string };
  error?: {
    name: ErrorName | string;
    sessionCookie?: string;
    userId?: string;
  };
  profileRedirect?: boolean;
  profileEditor?: ProfileEditor;
};

export interface NavigateOptionsWithState extends NavigateOptions {
  state?: ReactRouterLocationState;
}

export interface NavigateFunctionWithState extends NavigateFunction {
  (to: To, options?: NavigateOptionsWithState): void;

  (delta: number): void;
}

export type UtmParameters = {
  utm_source?: string;
  utm_medium?: 'external-tv';
  utm_campaign?: string;
  utm_content?: string;
};

export type SnowplowAnalyticsProperties = {
  getAppId: (mode: string) => string;
  collectorUrl: string;
  miniCollectorUrl: string;
  trackerNamespace: string;
};
